import type { Dispatch, SetStateAction } from 'react'
import { createPortal } from 'react-dom'
import SlideOver from 'src/components/ui/SlideOver'
import { Overlay } from 'src/components/ui/Overlay/Overlay'
import { useMobile } from 'src/hooks/useMobile'
import CloseIcon from 'src/images/icons/CloseIcon'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import { useAccountContext } from 'src/components/account/context'
import { useAddressContext } from 'src/components/checkout/steps/shipping/context/address-context'
import classNames from 'classnames'

import { PostalCodeModalContent } from './PostalCodeModalContent'
import { PostalCodeModalContentForGuest } from './PostalCodeModalContentForGuest'
import { usePostalCodeModalController } from '../../Hooks/usePostalCodeModalController'

interface PostalCodeModalProps {
  showModal: boolean
  setShowModal: Dispatch<SetStateAction<boolean>>
}

export const PostalCodeModal = ({
  showModal,
  setShowModal,
}: PostalCodeModalProps) => {
  const { isMobile } = useMobile()
  const { isLoggedIn } = useAccountContext()

  const { orderForm, setOrderForm } = useCheckout()
  const { setPostalCode, setAddress } = useAddressContext()

  const {
    inputValue,
    setInputValue,
    defaultAddressId,
    setDefaultAddressId,
    provisionalAddress,
    setProvisionalAddress,
    onModalClose,
    onSubmitPostalCode,
    onConfirmGuestPostalCode,
  } = usePostalCodeModalController({
    showModal,
    setShowModal,
    orderForm,
  })

  if (!showModal || isMobile === undefined) {
    return null
  }

  const Content = isLoggedIn ? (
    <PostalCodeModalContent
      onSubmit={onSubmitPostalCode}
      onModalClose={onModalClose}
      inputValue={inputValue}
      setInputValue={setInputValue}
      address={provisionalAddress}
      setProvisionalAddress={setProvisionalAddress}
      defaultAddressId={defaultAddressId}
      setDefaultAddressId={setDefaultAddressId}
      setPostalCode={setPostalCode}
      setAddress={setAddress}
    />
  ) : (
    <PostalCodeModalContentForGuest
      onModalClose={onModalClose}
      onConfirmGuestPostalCode={onConfirmGuestPostalCode}
      inputValue={inputValue}
      setInputValue={setInputValue}
      orderForm={orderForm}
      setOrderForm={setOrderForm}
      setPostalCode={setPostalCode}
      setProvisionalAddress={setProvisionalAddress}
      showModal={showModal}
    />
  )

  return (
    <>
      {isMobile ? (
        <SlideOver
          isOpen={showModal}
          onDismiss={() =>
            onModalClose({
              shouldTrackClose: true,
              buttonName: 'fora do modal',
              hasInputValue: inputValue.length > 0,
            })
          }
          size="custom"
          direction="rightSide"
          className={`flex flex-col bottom-0 transition-all duration-200 ease-in-out w-full ${
            isLoggedIn ? ' !h-[90%] ' : '!h-fit'
          }`}
        >
          <button
            className="absolute right-8 top-5"
            onClick={() =>
              onModalClose({
                shouldTrackClose: true,
                buttonName: 'Fechar',
                hasInputValue: inputValue.length > 0,
              })
            }
          >
            <CloseIcon width={24} height={24} />
          </button>
          <div
            className={classNames('mb-10 mt-16 overflow-auto', {
              'mx-4': !isLoggedIn,
            })}
          >
            {Content}
          </div>
        </SlideOver>
      ) : typeof document !== 'undefined' ? (
        createPortal(
          <>
            <Overlay
              show={showModal}
              onClick={() =>
                onModalClose({
                  shouldTrackClose: true,
                  buttonName: 'fora do modal',
                  hasInputValue: inputValue.length > 0,
                })
              }
            />
            <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4 pointer-events-none">
              <div
                className={classNames(
                  'pointer-events-auto relative flex flex-col gap-3 w-full max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain font-medium shadow-dropCard bg-postal-page rounded-3xl',
                  isLoggedIn ? 'max-w-[680px]' : 'max-w-[440px] p-6 pt-12'
                )}
              >
                {!isLoggedIn ? (
                  <button
                    type="button"
                    className="absolute right-5 top-5"
                    onClick={() =>
                      onModalClose({
                        shouldTrackClose: true,
                        buttonName: 'Fechar',
                        hasInputValue: inputValue.length > 0,
                      })
                    }
                  >
                    <span className="sr-only">Fechar</span>
                    <CloseIcon width={18} height={18} />
                  </button>
                ) : null}
                {Content}
              </div>
            </div>
          </>,
          document.body
        )
      ) : (
        <>
          <Overlay
            show={showModal}
            onClick={() =>
              onModalClose({
                shouldTrackClose: true,
                buttonName: 'fora do modal',
                hasInputValue: inputValue.length > 0,
              })
            }
          />
          <div
            className={classNames(
              "flex flex-col z-[10001] gap-3 w-full absolute font-medium shadow-dropCard bg-postal-page sm:mt-4 ml-4 sm:-ml-2 max-h-[calc(100dvh-10rem)] overflow-y-auto overscroll-contain before:content-[''] before:absolute before:w-4 before:h-4 before:bg-postal-page before:rounded-sm before:top-[-5px] before:left-[25px] before:-rotate-45",
              isLoggedIn ? 'max-w-[680px]' : 'p-4 max-w-[440px]'
            )}
          >
            {Content}
          </div>
        </>
      )}
    </>
  )
}
